import toNumber from 'lodash/toNumber'
import { SESSION_ID_KEY, SESSION_TIMEOUT_MS, USER_ID_KEY } from '../config/WebTelemetryConfig'
import { generateId, hasWindow, getWindow } from '@ztechapps/common.utils'

/**
 * Generates a unique session ID
 */
function generateSessionId(): string {
  return generateId()
}

/**
 * Manages browser session and user identification for telemetry
 */
export class SessionManager {
  private sessionId: string
  private userId: string | null = null
  private userAttributes: Record<string, string> = {}
  private customAttributes: Record<string, string> = {}
  private lastActivityTime: number
  private activityEventListeners: (() => void)[] = []

  constructor() {
    this.sessionId = this.getOrCreateSessionId()
    this.lastActivityTime = Date.now()
    this.loadUserFromStorage()
    this.setupActivityTracking()
  }

  /**
   * Cleanup all event listeners
   */
  destroy(): void {
    this.activityEventListeners.forEach((removeListener) => {
      removeListener()
    })
    this.activityEventListeners = []
  }

  /**
   * Get or create a session ID from sessionStorage
   */
  private getOrCreateSessionId(): string {
    if (!hasWindow()) {
      return generateSessionId()
    }

    try {
      const stored = sessionStorage.getItem(SESSION_ID_KEY)
      const storedTime = sessionStorage.getItem(`${SESSION_ID_KEY}_time`)

      if (stored && storedTime) {
        const lastTime = toNumber(storedTime)
        if (Date.now() - lastTime < SESSION_TIMEOUT_MS) {
          this.updateSessionTime()
          return stored
        }
      }

      // Create new session
      const newSessionId = generateSessionId()
      sessionStorage.setItem(SESSION_ID_KEY, newSessionId)
      this.updateSessionTime()
      return newSessionId
    } catch {
      // sessionStorage not available
      return generateSessionId()
    }
  }

  /**
   * Update session timestamp
   */
  private updateSessionTime(): void {
    try {
      sessionStorage.setItem(`${SESSION_ID_KEY}_time`, Date.now().toString())
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Load user info from localStorage
   */
  private loadUserFromStorage(): void {
    if (!hasWindow()) return

    try {
      const storedUser = localStorage.getItem(USER_ID_KEY)
      if (storedUser) {
        const parsed = JSON.parse(storedUser)
        this.userId = parsed.userId
        this.userAttributes = parsed.attributes || {}
      }
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Setup activity tracking to extend session on user interaction
   */
  private setupActivityTracking(): void {
    if (!hasWindow()) return

    const updateActivity = () => {
      this.lastActivityTime = Date.now()
      this.updateSessionTime()
    }

    // Track user activity to extend session
    // Store removeListener functions for cleanup
    const win = getWindow()
    win.addEventListener('click', updateActivity, { passive: true })
    win.addEventListener('keydown', updateActivity, { passive: true })
    win.addEventListener('scroll', updateActivity, { passive: true })

    // Store remove functions for cleanup - we need to use named functions for proper removal
    const boundUpdateActivity = updateActivity
    this.activityEventListeners.push(() => win.removeEventListener('click', boundUpdateActivity))
    this.activityEventListeners.push(() => win.removeEventListener('keydown', boundUpdateActivity))
    this.activityEventListeners.push(() => win.removeEventListener('scroll', boundUpdateActivity))
  }

  /**
   * Get current session ID
   */
  getSessionId(): string {
    // Check if session expired
    if (Date.now() - this.lastActivityTime > SESSION_TIMEOUT_MS) {
      this.sessionId = this.getOrCreateSessionId()
    }
    return this.sessionId
  }

  /**
   * Set user identification
   */
  setUser(userId: string, attributes?: Record<string, string>): void {
    this.userId = userId
    this.userAttributes = attributes || {}

    if (hasWindow()) {
      try {
        localStorage.setItem(
          USER_ID_KEY,
          JSON.stringify({
            userId,
            attributes: this.userAttributes,
          })
        )
      } catch {
        // Ignore storage errors
      }
    }
  }

  /**
   * Clear user identification (logout)
   */
  clearUser(): void {
    this.userId = null
    this.userAttributes = {}

    if (hasWindow()) {
      try {
        localStorage.removeItem(USER_ID_KEY)
      } catch {
        // Ignore storage errors
      }
    }
  }

  /**
   * Get current user ID
   */
  getUserId(): string | null {
    return this.userId
  }

  /**
   * Get user attributes
   */
  getUserAttributes(): Record<string, string> {
    return { ...this.userAttributes }
  }

  /**
   * Set a custom global attribute that will be included in all spans and metrics
   */
  setCustomAttribute(key: string, value: string): void {
    this.customAttributes[key] = value
  }

  /**
   * Remove a custom global attribute
   */
  removeCustomAttribute(key: string): void {
    delete this.customAttributes[key]
  }

  /**
   * Get all session/user attributes for spans
   */
  getContextAttributes(): Record<string, string> {
    const attrs: Record<string, string> = {
      'session.id': this.getSessionId(),
    }

    if (this.userId) {
      attrs['user.id'] = this.userId
    }

    // Add user attributes with user. prefix
    for (const [key, value] of Object.entries(this.userAttributes)) {
      attrs[`user.${key}`] = value
    }

    // Add custom attributes (e.g., openreplay.session.id)
    for (const [key, value] of Object.entries(this.customAttributes)) {
      attrs[key] = value
    }

    return attrs
  }
}
